/* eslint-disable react/jsx-curly-newline */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable operator-linebreak */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RegularButton, CustomCheckBox, CommonInput, AlertAdapter } from 'pg-ui-library';
import { useMutation } from '@tanstack/react-query';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { IPermission } from 'src/interfaces/employee';
import EmployeesService from 'src/services/employees.service';
import { useActions } from 'src/hooks/useActions';
import { ItemContainer } from '../../../employee-list/styles';
import { EmployeeAccessAttemptsTable } from './components/employee-access-attempts-table';
import { MFAImpersonateModal } from './components/mfa-impersonate-modal';
import {
  AccessAuthContainer,
  AccountDetailsButtonContainer,
  AccountDetailsContainer,
  Container,
  LastLoginText,
  TitleWrapper,
  TitleWrapperText,
} from './styles';

const EmployeePermissionBox: React.FC<{
  label: string;
  checked: boolean | undefined;
  disabled: boolean;
  changePermissionStatus: () => void;
}> = ({ label, checked, disabled, changePermissionStatus }) => (
  <CustomCheckBox
    label={label}
    disabled={disabled}
    checked={checked}
    onChange={() => {
      changePermissionStatus();
    }}
  />
);

const EmploymentLoginTab: React.FC = () => {
  const { t } = useTranslation();

  const { employee } = useTypedSelector(state => state.employees.data.selectedEmployee);

  const { loading } = useTypedSelector(state => state.employees);
  const { patchEmployeeAccess, disconnectEmployeeAccess } = useActions();

  const { config } = useTypedSelector(state => state.config.data);
  const [mfaModalOpen, setMfaModalOpen] = useState<boolean>(false);

  const mutationReset = useMutation(
    (variables: { companyId: string; employeeId: string }) =>
      EmployeesService.sendResetPasswordLink({
        companyId: variables.companyId,
        employeeId: variables.employeeId,
      }),
    {
      onSuccess: () => {
        AlertAdapter('Reset password link successfully sent!', 'success');
      },
      onError: () => {
        AlertAdapter('Failed to send reset password link', 'error');
      },
    }
  );

  // this block will be removed in the future
  const tryImpersonate = async () => {
    try {
      if (config?.selectedCompanyId && employee?.id) {
        const result = await EmployeesService.postImpersonationLink({
          employeeId: employee?.id,
          companyId: config?.selectedCompanyId,
          mfaCode: '999999',
        });
        window.open(result.data, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      AlertAdapter('Impersonate was not possible', 'error');
    }
  };

  const mutationSendSignup = useMutation(
    (variables: { companyId: string; employeeId: string }) =>
      EmployeesService.sendSignupLink({
        companyId: variables.companyId,
        employeeId: variables.employeeId,
      }),
    {
      onSuccess: () => {
        AlertAdapter('Signup link successfully sent!', 'success');
      },
      onError: () => {
        AlertAdapter('Failed to send signup email', 'error');
      },
    }
  );

  const sendSignupLink = async () => {
    if (config?.selectedCompanyId && employee?.id) {
      mutationSendSignup.mutate({
        companyId: config?.selectedCompanyId,
        employeeId: employee?.id,
      });
    }
  };

  const sendResetPaswordLink = async () => {
    if (config?.selectedCompanyId && employee?.id) {
      mutationReset.mutate({
        companyId: config?.selectedCompanyId,
        employeeId: employee?.id,
      });
    }
  };

  const disconnectEmployeeAccount = async () => {
    if (config?.selectedCompanyId && employee?.id) {
      disconnectEmployeeAccess(employee?.id, config?.selectedCompanyId);
    }
  };

  // const impersonation = async () => {
  //   setMfaModalOpen(true);
  // };

  const isAnActiveEmployee = useMemo(() => {
    if (employee?.accountStatus !== 'Active') {
      return false;
    }
    return true;
  }, [employee?.accountStatus]);

  const hasPermission = (permissionName: string) => {
    if (employee?.permissions) {
      return employee?.permissions.find((permission: IPermission) => permission.name === permissionName)?.isActive;
    }
    return false;
  };

  const getPermission = (permissionName: string) =>
    employee?.permissions.find((prm: IPermission) => prm.name === permissionName);

  const changePermissionStatus = (permissionName: string) => {
    const permission = getPermission(permissionName);
    patchEmployeeAccess({
      employeeId: employee?.id as unknown as string,
      permissionId: permission?.id as unknown as string,
      companyId: config?.selectedCompanyId as unknown as string,
    });
  };

  const handleMFAModalClose = () => {
    setMfaModalOpen(false);
  };

  return (
    <Container>
      <ItemContainer>
        <MFAImpersonateModal isOpen={mfaModalOpen} onClose={handleMFAModalClose} />
        <TitleWrapper>
          <TitleWrapperText>{t('employeePage.loginTab.accountDetails.title')}</TitleWrapperText>
          <AccountDetailsContainer>
            <div>
              <CommonInput
                disabled
                label="User ID"
                id="userId"
                name="userId"
                placeholder="User ID"
                value={employee?.userId}
              />
            </div>
            <div>
              <CommonInput
                disabled
                label="Account Status"
                id="accountStatus"
                name="accountStatus"
                placeholder="Account Status"
                value={employee?.accountStatus}
              />
            </div>
            <AccountDetailsButtonContainer>
              <RegularButton
                onClick={sendResetPaswordLink}
                disabled={!isAnActiveEmployee || mutationReset.isLoading || mutationReset.isSuccess || loading}
              >
                {t('employeePage.loginTab.accountDetails.resetButton')}
              </RegularButton>
            </AccountDetailsButtonContainer>
            <AccountDetailsButtonContainer>
              <RegularButton onClick={disconnectEmployeeAccount} disabled={!isAnActiveEmployee || loading}>
                {t('employeePage.loginTab.accountDetails.disconnectButton')}
              </RegularButton>
            </AccountDetailsButtonContainer>
            <AccountDetailsButtonContainer>
              <RegularButton
                onClick={sendSignupLink}
                disabled={
                  !!employee?.userId ||
                  isAnActiveEmployee ||
                  mutationSendSignup.isLoading ||
                  mutationSendSignup.isSuccess ||
                  loading
                }
              >
                {t('employeePage.loginTab.accountDetails.sendSignupEmail')}
              </RegularButton>
            </AccountDetailsButtonContainer>
          </AccountDetailsContainer>
        </TitleWrapper>
        <TitleWrapper>
          <TitleWrapperText>{t('employeePage.loginTab.accessAuth.title')}</TitleWrapperText>
          <AccessAuthContainer>
            <EmployeePermissionBox
              label="Profile"
              disabled={!isAnActiveEmployee}
              checked={hasPermission('hr_profile')}
              changePermissionStatus={() => changePermissionStatus('hr_profile')}
            />

            <EmployeePermissionBox
              label="Pay History"
              disabled={!isAnActiveEmployee}
              checked={hasPermission('pay_history')}
              changePermissionStatus={() => changePermissionStatus('pay_history')}
            />

            <EmployeePermissionBox
              label="Benefits"
              disabled={!isAnActiveEmployee}
              checked={hasPermission('benefits')}
              changePermissionStatus={() => changePermissionStatus('benefits')}
            />

            <EmployeePermissionBox
              label="Documents"
              disabled={!isAnActiveEmployee}
              checked={hasPermission('documents')}
              changePermissionStatus={() => changePermissionStatus('documents')}
            />

            <EmployeePermissionBox
              label="Tax Forms"
              disabled={!isAnActiveEmployee}
              checked={hasPermission('tax_forms')}
              changePermissionStatus={() => changePermissionStatus('tax_forms')}
            />

            <EmployeePermissionBox
              label="Invoice"
              disabled={!isAnActiveEmployee}
              checked={hasPermission('invoice')}
              changePermissionStatus={() => changePermissionStatus('invoice')}
            />

            <EmployeePermissionBox
              label="Expense"
              disabled={!isAnActiveEmployee}
              checked={hasPermission('expense')}
              changePermissionStatus={() => changePermissionStatus('expense')}
            />

            <EmployeePermissionBox
              label="Leave"
              disabled={!isAnActiveEmployee}
              checked={hasPermission('leave')}
              changePermissionStatus={() => changePermissionStatus('leave')}
            />
          </AccessAuthContainer>
          <div>
            <LastLoginText>{`Last login: ${employee?.lastLogin}`}</LastLoginText>
          </div>
          <div>
            <RegularButton
              // this block will be removed in the future by "impersonation"
              onClick={() => tryImpersonate()}
              disabled={!isAnActiveEmployee}
            >
              {t('employeePage.loginTab.accessAuth.impersonationButton')}
            </RegularButton>
          </div>
        </TitleWrapper>
      </ItemContainer>
      <ItemContainer>
        <TitleWrapper>
          <TitleWrapperText>{t('employeePage.loginTab.accessAttempts.title')}</TitleWrapperText>
          <div>
            <EmployeeAccessAttemptsTable />
          </div>
        </TitleWrapper>
      </ItemContainer>
    </Container>
  );
};

export default EmploymentLoginTab;
